import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Clock } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CATEGORY_META } from "@/i18n/translations";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useSeo } from "@/lib/seo";

export default function ListingDetail() {
  const { id } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => setError(true));
  }, [id]);

  useSeo({
    title: listing ? L(listing, "title") : "",
    description: listing ? L(listing, "summary") : "",
    lang,
    image: listing?.cover_image,
    type: "article",
    jsonLd: listing ? {
      "@context": "https://schema.org",
      "@type": "Offer",
      name: L(listing, "title"),
      description: L(listing, "summary"),
      category: listing.category,
      areaServed: listing.country || undefined,
    } : undefined,
  });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!listing) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;

  const meta = CATEGORY_META[listing.category];
  const v = listing.verification || {};
  const labels = t("verification_levels");

  return (
    <article className="mx-auto max-w-4xl px-6 py-14 lg:px-10">
      <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne">
        <ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Retour" : "Back"}
      </button>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link to={`/category/${listing.category}`} className="text-xs uppercase tracking-[0.2em] text-champagne">
          {meta ? meta[lang] : listing.category}
        </Link>
        <VerifiedBadge verified={listing.rst_verified} size="lg" />
      </div>

      <h1 className="mb-4 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{L(listing, "title")}</h1>
      <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(listing, "summary")}</p>

      {listing.cover_image && (
        <div className="mb-10 grain-overlay aspect-[16/9] overflow-hidden bg-sand">
          <img src={listing.cover_image} alt={L(listing, "title")} className="h-full w-full object-cover" />
        </div>
      )}

      <div className="mb-10 flex flex-wrap gap-x-8 gap-y-3 border-y border-tertiary py-5 text-sm">
        {listing.sector && <span className="inline-flex items-center gap-2 text-anthracite/70"><Tag className="h-4 w-4 text-champagne" strokeWidth={1.5} />{listing.sector}</span>}
        {(listing.country || listing.region) && <span className="inline-flex items-center gap-2 text-anthracite/70"><MapPin className="h-4 w-4 text-champagne" strokeWidth={1.5} />{[listing.region, listing.country].filter(Boolean).join(", ")}</span>}
        {listing.intent && <span className="text-anthracite/70">{t(`intent_${listing.intent}`)}</span>}
      </div>

      {L(listing, "description") && (
        <div className="prose mb-12 max-w-none whitespace-pre-line text-base leading-relaxed text-anthracite/80">
          {L(listing, "description")}
        </div>
      )}

      {listing.tags?.length > 0 && (
        <div className="mb-12 flex flex-wrap gap-2">
          {listing.tags.map((tag) => (
            <span key={tag} className="rounded-full border border-tertiary bg-sand px-3 py-1 text-xs text-anthracite/60">{tag}</span>
          ))}
        </div>
      )}

      {/* Verification panel */}
      {v.level && v.level !== "unverified" && (
        <div className="mb-12 border border-champagne/20 bg-champagne/5 p-6" data-testid="verification-panel">
          <h3 className="mb-4 font-serif text-xl text-anthracite">{t("verification")}</h3>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-anthracite/50">{t("verification_level")}</dt>
              <dd className="font-medium text-anthracite">{labels[v.level] || v.level}</dd>
            </div>
            {v.last_verified_at && (
              <div>
                <dt className="text-anthracite/50">{t("last_verified")}</dt>
                <dd className="inline-flex items-center gap-1.5 font-medium text-anthracite">
                  <Clock className="h-3.5 w-3.5 text-champagne" />
                  {new Date(v.last_verified_at).toLocaleDateString(lang)}
                </dd>
              </div>
            )}
            {L(v, "source") && (
              <div className="sm:col-span-2">
                <dt className="text-anthracite/50">{t("source")}</dt>
                <dd className="text-anthracite/80">{L(v, "source")}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      <div className="flex flex-col items-start gap-4 border-t border-tertiary pt-10 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-anthracite/60">{t("connect_sub")}</p>
        <Link
          to={`/connect?category=${listing.category}`}
          data-testid="detail-connect-cta"
          className="bg-anthracite px-8 py-4 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-black"
        >
          {t("cta_connect")}
        </Link>
      </div>
    </article>
  );
}
